import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { AdminShell } from '@/components/AdminShell'
import { AppShell } from '@/components/AppShell'
import { AdminAuthProvider, useAdminAuth } from '@/context/AdminAuthContext'
import { useAuth } from '@/context/AuthContext'
import { Account } from '@/pages/Account'
import { Admin } from '@/pages/Admin'
import { Dashboard } from '@/pages/Dashboard'
import { Jobs } from '@/pages/Jobs'
import { BannerStudio } from '@/pages/BannerStudio'
import { BatchStudio } from '@/pages/BatchStudio'
import { DetailPageStudio } from '@/pages/DetailPageStudio'
import { FashionStudioRoute } from '@/pages/FashionStudio'
import { ImageTools } from '@/pages/ImageTools'
import { AdminLogin, Login } from '@/pages/Login'
import { AiModels } from '@/pages/AiModels'
import { SocialImageStudio } from '@/pages/SocialImageStudio'
import { VideoStudio } from '@/pages/VideoStudio'
import { DigitalHumanStudio } from '@/pages/DigitalHumanStudio'
import { CopyStudio } from '@/pages/CopyStudio'
import { TtsStudio } from '@/pages/TtsStudio'
import { MyWorks } from '@/pages/MyWorks'
import { ProductAssets } from '@/pages/ProductAssets'
import { UsageAudit } from '@/pages/UsageAudit'

/**
 * 登录闸门。
 *
 * 会员体系（2026-10-07）之后，**没有账号就没有任何东西可看**：任务、作品、素材、
 * 用量、额度全都按账号收口（后端的每个业务接口都 `require_user`）。所以未登录时
 * 不是「少显示几个按钮」，而是整块工作台都走不通 —— 直接把 `AppShell` 换成登录页。
 *
 * ⚠️ 判定顺序：**先看 booting，再看 user**。反过来的话，刷新页面那一瞬
 * `user` 还是 null（会话还没验完），用户会**先看到登录页再被弹回工作台** ——
 * 一闪而过的登录页看起来就像「登录又掉了」。
 *
 * ⚠️ 这里**不再有 `/admin` 的特例**（2026-10-07 晚）：后台已挪到自己的路由
 * （见下面的 `AdminRoute`），压根不经过这道闸门。留着那段 `wantsAdmin` 分支
 * 就成了永远不会执行的死代码 —— 而死代码最坏的地方是它会「看起来还在生效」，
 * 下一个人改后台入口时会照着它改一遍，然后发现没用。
 */
function RequireAuth() {
  const { user, booting } = useAuth()
  const location = useLocation()
  const from = location.pathname + location.search

  if (booting) {
    // 引导中：只放一个骨架，**不渲染 AppShell**（它会立刻打一堆业务接口，
    // 而那会儿 token 还没验完，401 会在屏幕上堆一排报错）
    return <BootSkeleton />
  }
  if (!user) {
    // 把来源路径带上：登录成功后回到原来那一页，而不是一律甩回首页。
    return <Navigate to="/login" replace state={{ from }} />
  }
  return <AppShell />
}

/**
 * 后台管理（`/admin`）—— **独立整页，不套工作台外壳**。
 *
 * 用户口径（2026-10-07 晚）：「http://127.0.0.1:5273/admin，我希望是新页面的，
 * 不是嵌入之前的页面中」。
 *
 * 在此之前 `/admin` 是 `RequireAuth` 的子路由 ⇒ 渲染在 `AppShell` 的 `Outlet` 里：
 * 左边仍是图片/视频的侧栏、顶栏仍是模板中心/我的作品，后台看起来只是「工作台里
 * 换了一块内容」；更别扭的是登录那一跳 —— 未登录时给的是独立的管理员登录页
 * （整屏、无外壳），一登进去却换回带外壳的后台，同一件事前后两副样子。
 *
 * ### 🔴 它管的是**后台那一侧**的会话，跟会员登录无关（2026-10-07 拆表）
 *
 * 上游是 `useAdminAuth()` 而不是 `useAuth()`。这一条是整个「两套登录态」的落点：
 *
 *   · 一个**从没登录过**会员的浏览器，只要后台 token 还在，敲 `/admin` 就能进——
 *     不必先去工作台登一次；
 *   · 反过来，**在工作台登着会员、后台没登**的人，敲 `/admin` 拿到的是后台登录页
 *     （而不是会员后台的一屏 403）。他会看到自己要的是另一套账号 ——
 *     这正是要的效果，而不是一个 bug；
 *   · 顺手退出后台**不会**把工作台踢下线（两处 token / 会话各清各的）。
 *
 * ### 三段
 *
 *   ① 会话校验中 → 骨架（与 `RequireAuth` 同一枚，别在刷新瞬间闪登录页）；
 *   ② 没登录 → **原地**后台登录页（地址栏保持 `/admin`，不跳 `/login`）；
 *   ③ 登录了 → `AdminShell` + `Admin` 页。
 *
 * ⚠️ **没有「普通成员走这一段」的第四种情况了** —— 拆表前会员表里的 `admin`
 * 那一行能登进来、非管理员登进来则渲染「需要管理员权限」。现在 `AdminRoute`
 * 只在**后台会话已建立**时往下渲染，所以那个分支既不可达、也删掉了
 * （见 `pages/Admin.tsx` 头部注释）。真闸门仍在后端（`/api/admin/*` 全部
 * `require_admin`），前端这层只是路由。
 */
function AdminRoute() {
  const { admin, booting } = useAdminAuth()
  if (booting) return <BootSkeleton />
  if (!admin) return <AdminLogin />
  return (
    <AdminShell>
      <Admin />
    </AdminShell>
  )
}

/** 会话校验中的骨架：工作台与后台共用一份（两处各写一个必然长得不一样）。 */
function BootSkeleton() {
  return (
    <div className="boot">
      <span className="spinner" />
      <span className="text-sm muted">正在校验登录状态…</span>
    </div>
  )
}

export function App() {
  return (
    <Routes>
      {/* 登录页在工作台外壳**之外**：侧栏与顶栏里的每个入口都需要账号 */}
      <Route path="/login" element={<Login />} />

      {/* 后台管理同样在外壳之外 —— 它是另一张页面，不是工作台的一个标签。
          未登录时由 `AdminRoute` 原地给后台登录页。

          ⚠️ `AdminAuthProvider` **只包这一条路由**，不挂到 `main.tsx` 的
          Provider 树上。挂到全局的话，每个访客（包括只看工作台的人）一进站
          就会去问一次 `/api/admin/auth/session` —— 一次注定 401 的请求，
          而它的价值只对 `/admin` 那一页存在。Provider 的作用域跟着用途走：
          谁需要什么时候问，谁负责挂。 */}
      <Route
        path="/admin"
        element={
          <AdminAuthProvider>
            <AdminRoute />
          </AdminAuthProvider>
        }
      />

      <Route element={<RequireAuth />}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/jobs" element={<Jobs />} />
        {/* 任务详情**没有独立路由**：宽视图是任务卡从右边滑出来的那一层抽屉
            （见 TaskCard 的 TaskDrawer），地址栏不变、不离开当前列表。
            曾经做过 `/jobs/:jobId` 的独立页面，用户明确否掉了 ——
            「你就不能弹窗或者右边展开吗」。 */}
        <Route path="/banner" element={<BannerStudio />} />
        {/* 主图 / 商品套图 / 详情页 / 平台详情模块 / 模仿版式 —— **一个页面**，
            模式由页签外的 chip-row 切换（2026-09-29 用户要求「这 3 个页面合并为一个」）。
            下面两条是**旧地址别名**，不是页面：老链接（书签、文档、聊天记录）进来
            自动落到对应的模式上，而不是掉进 `*` 兜底回首页。
            别名不算「第二个入口」—— 侧栏只有一条，页面也只有一份。 */}
        <Route path="/detail-page" element={<DetailPageStudio />} />
        {/*
          批量出图工作台 —— **独立页面**，不是详情页的一个形态（2026-09-29 用户口径：
          「批量处理这个需要新页面，这样一来，我可以后补图片，在这个页面继续改不现实」）。

          为什么它值得一条自己的路由，而不是「详情页 + 一个 Segmented」：
          · **寿命不同** —— 详情页的状态刷新即失，批次要能关掉页面、明天回来接着补图；
          · **输入物相反**（铁律 6 明文允许独立成页的那条）—— 那边输入「一个产品的
            几个字段」，这边输入「一份表格 + 一整个文件夹的图」，产出 N 个任务。
          两条都成立，所以拆页；详情页页签① 只留一行指路（不留可点的切换钮，
          否则就是同一字段两个入口，且两处状态不同步）。
        */}
        <Route path="/batch" element={<BatchStudio />} />
        {/* `/ai-set` 原本直达「商品套图」。该形态 2026-09-30 下架
            （用户原话「这套图跟详情页重复了」—— 它出的图与「平台详情模块」
            是同一批），所以没有「对应的形态」可落了，只回本页默认形态。
            这条别名**不能删**：删了老链接会掉进 `*` 兜底静默回首页。 */}
        <Route path="/ai-set" element={<Navigate to="/detail-page" replace />} />
        {/* `/localize` 原本直达「成片派生」，该能力 2026-09-29 整块下架
            （用户原话「派生这个弄太复杂了，不要了」）—— 它同样没有「对应的模式」可落，
            所以也只回本页默认形态。这条别名**不能删**：删了老链接会掉进 `*` 兜底静默回首页。 */}
        <Route path="/localize" element={<Navigate to="/detail-page" replace />} />
        <Route path="/professional/:category" element={<FashionStudioRoute />} />
        <Route path="/img-localize" element={<ImageTools />} />
        <Route path="/social-image" element={<SocialImageStudio />} />
        {/* 带货短视频 + 商品视频 —— **一个页面**（2026-09-29 用户要求「带货短视频跟商品视频合并」），
            2026-10-05 连页面里那排档位 chip 也撤了：两档并成一档（用户原话「感觉差不多啊」）。
            下面两条是**旧地址别名**，不是页面：老书签与分享链接进来自动落到合并档
            （`?mode=main-video` 由 `getStudioVideoMode()` 回落，不报错）。
            ⚠️ 别名与 `?mode=` 的兼容**别删** —— 并掉的是档位，不是别人手里的链接。
            视频复刻**不并入**（用户原话「视频复刻不变」）：它的输入物相反
            （必传参考视频，是复刻既有结构），所以仍是独立页面、独立任务键。 */}
        <Route path="/video-studio" element={<VideoStudio />} />
        <Route path="/main-video" element={<Navigate to="/video-studio?mode=main-video" replace />} />
        <Route path="/short-video" element={<Navigate to="/video-studio?mode=short-video" replace />} />
        <Route path="/video-replicate" element={<VideoStudio />} />
        <Route path="/digital-human" element={<DigitalHumanStudio />} />
        <Route path="/copywriting" element={<CopyStudio />} />
        {/* 语音合成（2026-10-08）—— 口播稿 → mp3，挂在侧栏「② 文案」组下。
            它是**独立页面**而不是文案页的一个页签：文案页产出的是「给人看的字」
            （要按平台分渠道、要拷进后台改措辞），这里产出的是「给机器读的声音」，
            两边的必填项、校验与右栏解释卡没有一件是共用的。
            输入物也相反（那边要产品名/卖点/人群，这边只要一段稿子 + 一个音色），
            正是本仓铁律里明文允许独立成页的那种情形。 */}
        <Route path="/tts" element={<TtsStudio />} />
        {/* 模板中心**没有独立页面**：2026-09-29 用户要求「首页改为模板中心为主」，
            墙搬到了首页（`components/TemplateGallery.tsx`），顶栏「模板中心」直接落在首页。
            本路由留作**旧地址别名** —— 老书签与聊天记录里的 /hot-styles 不 404，
            也不会掉进 `*` 兜底静默回首页。页面只有一份，别名不算第二个入口。 */}
        <Route path="/hot-styles" element={<Navigate to="/" replace />} />
        <Route path="/my-works" element={<MyWorks />} />
        {/* AI 模特库（2026-10-07）—— 顶栏那颗「AI 模特」的落点。
            在那之前它直指 `/professional/products?feature=product-model`，
            也就是**生成表单**；用户口径是「点击这个 AI 模特应该去一个新页面，
            是全部已经生成的 AI 模特的页面，可以搜索跟筛选的」。
            生成入口没有消失，挪进了这一页的「生成新模特」（见 pages/AiModels.tsx）。 */}
        <Route path="/ai-models" element={<AiModels />} />
        {/* 商品资产（2026-10-08）—— 顶栏那颗「商品库」的落点。
            **一个商品一条档案**（标题 / 卖点 / 语言 / 组图），支持单个新增与
            表格批量导入；「主图 · 详情」页直接引用它，用户不必再自己输出
            产品图与文案。放在 `RequireAuth` 里面：它是按账号收口的私有数据，
            未登录进来只会拿到一屏空列表（顶栏那排按钮此时本来也不渲染）。 */}
        <Route path="/product-assets" element={<ProductAssets />} />
        <Route path="/usage-audit" element={<UsageAudit />} />
        {/* ⚠️ `/admin` **不在这里** —— 它已经在上面注册成独立整页（`AdminRoute`）。
            塞回这个分组就是「后台被关进工作台外壳」那个问题复发，
            `smoke-nav.ts` 有一条断言专门盯着这件事。 */}
        {/* 账号中心（2026-10-07）。入口在**顶栏右上角的账号胶囊下拉**里
            （`surface: 'account'`，见 config/nav.ts），不占顶栏直链位。
            它是「额度账本」那个弹层升上来的整页：账号信息 + 可用/已用额度 + 完整账本。
            放在 `RequireAuth` 里面 —— 页面上的一切都按账号收口，没登录进来只会
            拿到一屏空数字（顶栏那枚胶囊此时本来也是空的）。 */}
        <Route path="/account" element={<Account />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  )
}
